import React, { useCallback, useMemo, useReducer, useEffect, memo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import jobService from '../../utils/jobService';
import cacheService from '../../services/cacheService';
import reportService from '../../services/reportService';
import { extractJobIdFromSlug } from '../../utils/slugify';
import { formatHourlySalaryRange, getSalaryTooltip, formatSalaryRange } from '../../utils/currencyUtils';
import formService from '../../services/formService';
import Button from '../../components/ui/Button';
import ApplicationModal from './components/ApplicationModal';

import JobDescription from './components/JobDescription';
import JobRequirements from './components/JobRequirements';
import CompanyInfo from './components/CompanyInfo';
import SimilarJobs from './components/SimilarJobs';
import LoadingSkeleton from './components/LoadingSkeleton';
import ErrorState from './components/ErrorState';
import JobDetailsSEO from './components/JobDetailsSEO';
import JobDetailsHeader from './components/JobDetailsHeader';
import ReportModal from './components/ReportModal';
import ReportSuccessModal from './components/ReportSuccessModal';

// State reducer for better state management
const initialState = {
  job: null,
  loading: true,
  error: null,
  isBookmarked: false,
  showApplicationModal: false,
  similarJobs: [],
  hasApplied: false,
  applicationStatus: null,
  showReportModal: false,
  reportSubmitting: false,
  reportSuccess: false,
  reportError: null,
  selectedReason: '',
  hasReportedJob: false,
  checkingReport: false
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_JOB':
      return { ...state, job: action.payload, loading: false, error: null };
    case 'SET_BOOKMARK':
      return { ...state, isBookmarked: action.payload };
    case 'SET_APPLICATION_STATE':
      return { 
        ...state, 
        hasApplied: action.payload.hasApplied, 
        applicationStatus: action.payload.status 
      };
    case 'SET_SIMILAR_JOBS':
      return { ...state, similarJobs: action.payload };
    case 'TOGGLE_APPLICATION_MODAL':
      return { ...state, showApplicationModal: action.payload };
    case 'SET_REPORT_STATE':
      return { ...state, ...action.payload };
    case 'RESET_REPORT':
      return {
        ...state,
        showReportModal: false,
        selectedReason: '',
        reportError: null,
        reportSuccess: false,
        hasReportedJob: false,
        reportSubmitting: false,
        checkingReport: false
      };
    default:
      return state;
  }
};

const JobDetails = memo(() => {
  const { jobSlug, jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile } = useAuth();
  
  // Use reducer for better state management
  const [state, dispatch] = useReducer(reducer, initialState);
  
  // Extract actual job ID from slug or use direct jobId
  const actualJobId = useMemo(() => {
    // Check URL params first, then query params
    if (jobSlug) return extractJobIdFromSlug(jobSlug);
    if (jobId) return jobId;
    return searchParams.get('jobId');
  }, [jobSlug, jobId, searchParams]);
  
  // Auto-open application modal if apply=true in URL
  const shouldOpenApplication = useMemo(() => 
    searchParams.get('apply') === 'true'
  , [searchParams]);

  // Handle redirection when job is not found
  useEffect(() => {
    if (state.error && !state.loading) {
      // Immediately redirect if there's no job ID (direct navigation)
      if (!actualJobId) {
        navigate('/job-search', { 
          state: { 
            errorMessage: 'Invalid job ID. Please browse available jobs.',
            fromJobDetails: true
          } 
        });
        return;
      }
      
      // Otherwise, show error message for a few seconds before redirecting
      const redirectTimer = setTimeout(() => {
        navigate('/job-search', { 
          state: { 
            errorMessage: 'The job you were looking for could not be found.',
            fromJobDetails: true
          } 
        });
      }, 3000); // Redirect after 3 seconds
      
      return () => clearTimeout(redirectTimer);
    }
  }, [state.error, state.loading, navigate, actualJobId]);

  // Load similar jobs based on skills - memoized to prevent unnecessary calls
  const loadSimilarJobs = useCallback(async (skills, currentJobId) => {
    try {
      const result = await jobService.getJobs({
        skills: skills.slice(0, 2), // Use first 2 skills
        limit: 4
      });

      if (result?.success) {
        // Filter out current job using full UUID
        const filtered = result.data?.filter(j => j.id !== currentJobId) || [];
        dispatch({ type: 'SET_SIMILAR_JOBS', payload: filtered.slice(0, 3) });
      }
    } catch (error) {
      // Error handled silently
      console.warn('Failed to load similar jobs:', error);
    }
  }, []);

  // Check if user has already applied to this job - optimized with error handling
  const checkApplicationStatus = useCallback(async (jobId, userId) => {
    if (!userId || !jobId) return;
    
    try {
      const result = await jobService.checkUserApplicationStatus(userId, jobId);
      if (result?.success) {
        dispatch({ 
          type: 'SET_APPLICATION_STATE', 
          payload: { 
            hasApplied: result.hasApplied, 
            status: result.application?.status || null 
          } 
        });
      } else {
        // If the check fails, assume not applied to avoid blocking the user
        dispatch({ 
          type: 'SET_APPLICATION_STATE', 
          payload: { hasApplied: false, status: null } 
        });
      }
    } catch (error) {
      // If there's an error, assume not applied to avoid blocking the user
      console.warn('Application status check failed:', error);
      dispatch({ 
        type: 'SET_APPLICATION_STATE', 
        payload: { hasApplied: false, status: null } 
      });
    }
  }, []);

  // Load job details using callback to prevent dependency issues - optimized
  const loadJobDetails = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      // Check if actualJobId is valid
      if (!actualJobId) {
        dispatch({ type: 'SET_ERROR', payload: 'Invalid job ID. Redirecting to job search page...' });
        return;
      }

      // Use getJobWithFullDetails which supports both UUID and short ID formats
      const result = await jobService.getJobWithFullDetails(actualJobId);

      if (!result?.success) {
        dispatch({ type: 'SET_ERROR', payload: result?.error || 'Failed to load job details' });
        return;
      }

      dispatch({ type: 'SET_JOB', payload: result.data });
      
      // Track recently viewed job (for authenticated VAs only)
      if (user?.id && userProfile?.role === 'va' && cacheService.isAvailable()) {
        try {
          await cacheService.addRecentlyViewedJob(user.id, result.data.id);
        } catch (err) {
          console.warn('Failed to track recently viewed job:', err);
        }
      }
      
      // Load similar jobs based on skills
      if (result.data?.skills_required?.length > 0) {
        loadSimilarJobs(result.data.skills_required, result.data.id);
      }

      // Check if job is bookmarked (for authenticated users)
      if (user) {
        // This would be handled by the bookmark service
        // For now, we'll set it to false
        dispatch({ type: 'SET_BOOKMARK', payload: false });
        
        // Check if user has already applied to this job
        await checkApplicationStatus(result.data.id, user.id);
      }
    } catch (err) {
      console.error('Error loading job details:', err);
      dispatch({ type: 'SET_ERROR', payload: 'Something went wrong while loading job details' });
    }
  }, [actualJobId, user, userProfile?.role, loadSimilarJobs, checkApplicationStatus]);

  useEffect(() => {
    if (actualJobId) {
      loadJobDetails();
    }
  }, [actualJobId, loadJobDetails]);

  useEffect(() => {
    const handleAutoOpenApplication = async () => {
      if (shouldOpenApplication && state.job && user) {
        // Check if job has forms before auto-opening
        try {
          const formsResult = await formService.getJobForms(state.job.id);
          
          if (formsResult?.success && formsResult.data && formsResult.data.length > 0) {
            // Job has forms - redirect to dedicated application page
            const slug = state.job.public_slug || state.job.id;
            navigate(`/job-application/${slug}`, { replace: true });
          } else {
            // No forms - show modal
            dispatch({ type: 'TOGGLE_APPLICATION_MODAL', payload: true });
          }
        } catch (error) {
          console.error('Error checking for forms:', error);
          // On error, default to showing modal
          dispatch({ type: 'TOGGLE_APPLICATION_MODAL', payload: true });
        }
      }
    };

    handleAutoOpenApplication();
  }, [shouldOpenApplication, state.job, user, navigate]);

  // Check if job has expired (past application_deadline or status is expired)
  const isJobExpired = useMemo(() => {
    if (!state.job) return false;
    if (state.job.status === 'expired') return true;
    if (state.job.application_deadline && new Date(state.job.application_deadline) < new Date()) return true;
    return false;
  }, [state.job]);

  // Memoized handlers for better performance
  const handleApply = useCallback(async () => {
    if (!user) {
      // Redirect to login with return URL to this job detail page with apply param
      navigate(`/login-register?redirect=${encodeURIComponent(window.location.pathname + '?apply=true')}`);
      return;
    }

    // Block applications on expired jobs
    if (isJobExpired) {
      return;
    }

    // If user has already applied, don't open the modal
    if (state.hasApplied) {
      return;
    }

    // Check if job has attached forms
    try {
      const formsResult = await formService.getJobForms(state.job?.id);
      
      if (formsResult?.success && formsResult.data && formsResult.data.length > 0) {
        // Job has forms - redirect to dedicated application page
        const slug = state.job.public_slug || state.job.id;
        navigate(`/job-application/${slug}`);
      } else {
        // No forms - show modal
        dispatch({ type: 'TOGGLE_APPLICATION_MODAL', payload: true });
      }
    } catch (error) {
      console.error('Error checking for forms:', error);
      // On error, default to showing modal
      dispatch({ type: 'TOGGLE_APPLICATION_MODAL', payload: true });
    }
  }, [user, state.hasApplied, state.job, navigate, isJobExpired]);

  const handleBookmark = useCallback(async () => {
    if (!user || !state.job) {
      // Redirect to login with return URL to this job detail page
      navigate(`/login-register?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    try {
      const result = await jobService.toggleBookmark(user.id, state.job.id);
      
      if (result?.success) {
        dispatch({ type: 'SET_BOOKMARK', payload: result.bookmarked });
      }
    } catch (error) {
      // Error handled silently
      console.warn('Bookmark toggle failed:', error);
    }
  }, [user, state.job, navigate]);

  const handleReportJob = useCallback(async () => {
    if (!user) {
      navigate(`/login-register?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    // Check if user has already reported this job
    if (state.job?.id) {
      dispatch({ type: 'SET_REPORT_STATE', payload: { checkingReport: true } });
      try {
        const result = await reportService.checkExistingReport(user.id, state.job.id, 'job');
        if (result.success) {
          dispatch({ type: 'SET_REPORT_STATE', payload: { hasReportedJob: result.hasReported } });
        }
      } catch (error) {
        console.error('Error checking existing report:', error);
        // Continue to show modal even if check fails
      } finally {
        dispatch({ type: 'SET_REPORT_STATE', payload: { checkingReport: false } });
      }
    }

    dispatch({ type: 'SET_REPORT_STATE', payload: { showReportModal: true } });
  }, [user, state.job, navigate]);

  const handleSubmitReport = useCallback(async () => {
    if (!state.selectedReason.trim()) {
      dispatch({ type: 'SET_REPORT_STATE', payload: { reportError: 'Please select a reason for reporting this job.' } });
      return;
    }

    dispatch({ type: 'SET_REPORT_STATE', payload: { reportSubmitting: true, reportError: null } });

    try {
      const description = document.getElementById('reportDescription')?.value || '';
      
      const result = await reportService.submitJobReport({
        jobId: state.job.id,
        reportedBy: user.id,
        reason: state.selectedReason,
        description: description
      });

      if (result.success) {
        dispatch({ type: 'SET_REPORT_STATE', payload: { reportSuccess: true } });
      } else {
        dispatch({ type: 'SET_REPORT_STATE', payload: { reportError: result.error || 'Failed to submit report. Please try again.' } });
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      dispatch({ type: 'SET_REPORT_STATE', payload: { reportError: 'An unexpected error occurred. Please try again.' } });
    } finally {
      dispatch({ type: 'SET_REPORT_STATE', payload: { reportSubmitting: false } });
    }
  }, [state.selectedReason, state.job, user]);

  const resetReportModal = useCallback(() => {
    dispatch({ type: 'RESET_REPORT' });
    // Clear the textarea
    const textarea = document.getElementById('reportDescription');
    if (textarea) textarea.value = '';
  }, []);

  const handleApplicationSubmit = useCallback(async (applicationData) => {
    const result = await jobService.applyToJob({
      job_id: state.job.id,
      applicant_id: user.id,
      cover_letter: applicationData.cover_letter,
      proposed_rate: applicationData.proposed_rate_max || applicationData.proposed_rate_min || applicationData.proposed_rate,
      proposed_rate_min: applicationData.proposed_rate_min,
      proposed_rate_max: applicationData.proposed_rate_max,
      availability_start: applicationData.availability_start,
      portfolio_links: applicationData.portfolio_links || [],
      formResponses: applicationData.formResponses,
      formId: applicationData.formId
    });

    if (result?.success) {
      dispatch({ type: 'TOGGLE_APPLICATION_MODAL', payload: false });
      // Update local state to reflect that user has applied
      dispatch({ 
        type: 'SET_APPLICATION_STATE', 
        payload: { hasApplied: true, status: 'pending' } 
      });
      // Show success message or redirect
      navigate('/applications');
    } else {
      throw new Error(result?.error || 'Failed to submit application');
    }
  }, [state.job, user, navigate]);

  // Enhanced salary formatting with currency conversion (hourly-based) - memoized
  const formatSalary = useCallback((hourlyMin, hourlyMax, jobType, jobCurrency = 'USD') => {
    // Get user's preferred currency from profile (fallback to USD)
    const userCurrency = userProfile?.preferred_currency || 'USD';
    
    return formatHourlySalaryRange({
      hourlyMin,
      hourlyMax,
      jobType,
      jobCurrency,
      userCurrency,
      showConversionNote: false
    });
  }, [userProfile?.preferred_currency]);

  // Get tooltip for salary calculation explanation - memoized
  const getSalaryTooltipText = useCallback((hourlyMin, hourlyMax, jobType, jobCurrency = 'USD') => {
    // Get user's preferred currency from profile (fallback to USD)
    const userCurrency = userProfile?.preferred_currency || 'USD';
    
    return getSalaryTooltip({
      hourlyMin,
      hourlyMax,
      jobType,
      jobCurrency,
      userCurrency
    });
  }, [userProfile?.preferred_currency]);

  // Memoized utility functions
  const getJobTypeLabel = useMemo(() => (type) => {
    const labels = {
      'full_time': 'Full Time',
      'part_time': 'Part Time',
      'contract': 'Contract',
      'freelance': 'Freelance'
    };
    return labels[type] || type;
  }, []);

  const getExperienceLabel = useMemo(() => (level) => {
    const labels = {
      'entry': 'Entry Level',
      'intermediate': 'Intermediate',
      'senior': 'Senior',
      'expert': 'Expert'
    };
    return labels[level] || level;
  }, []);

  const handleEditJob = useCallback(() => {
    if (state.job?.id && user?.id && state.job?.employer_id === user.id) {
      navigate(`/job-posting/${state.job.id}`);
    }
  }, [navigate, state.job?.id, state.job?.employer_id, user?.id]);
  // SEO data preparation - memoized for performance
  const seoData = useMemo(() => {
    if (!state.job) return {};

    const title = `${state.job.title} at ${state.job.company_name} | VA Sprouts Jobs`;
    const description = state.job.description 
      ? `${state.job.description.substring(0, 155)}...` 
      : `Apply for ${state.job.title} position at ${state.job.company_name}. ${state.job.location} • ${getJobTypeLabel(state.job.job_type)} • ${formatSalary(state.job.budget_min, state.job.budget_max, state.job.job_type, state.job.currency)}`;
    
    const url = window.location.href;
    const image = state.job?.employer?.company_logo || state.job?.employer?.profile_picture || '/favicon.ico';

    return {
      title,
      description,
      url,
      image,
      structuredData: {
        "@context": "https://schema.org/",
        "@type": "JobPosting",
        "title": state.job.title,
        "description": state.job.description,
        "identifier": {
          "@type": "PropertyValue",
          "name": "VA Sprouts Job ID",
          "value": state.job.id
        },
        "datePosted": state.job.created_at,
        "validThrough": state.job.application_deadline || new Date(new Date(state.job.created_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        "employmentType": state.job.job_type?.toUpperCase(),
        "hiringOrganization": {
          "@type": "Organization",
          "name": state.job.company_name,
          "sameAs": state.job.employer?.website,
          "logo": state.job.employer?.company_logo || state.job.employer?.profile_picture
        },
        "jobLocation": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": state.job.location,
            "addressCountry": "US"
          }
        },
        "baseSalary": state.job.budget_min || state.job.budget_max ? {
          "@type": "MonetaryAmount",
          "currency": state.job.currency || "USD",
          "value": {
            "@type": "QuantitativeValue",
            "minValue": state.job.budget_min,
            "maxValue": state.job.budget_max,
            "unitText": "HOUR"
          }
        } : undefined,
        "experienceRequirements": state.job.experience_level,
        "skills": state.job.skills_required,
        "workHours": state.job.job_type === 'full_time' ? '40 hours per week' : undefined,
        "applicationDeadline": state.job.application_deadline,
        "industry": state.job.employer?.industry
      }
    };
  }, [state.job, getJobTypeLabel, formatSalary]);

  // Loading state with skeleton
  if (state.loading) {
    return (
      <LoadingSkeleton />
    );
  }

  // Error state
  if (state.error) {
    return (
      <ErrorState error={state.error} navigate={navigate} />
    );
  }

  return (
    <>
      {/* SEO Head Tags */}
      {state.job && (
        <JobDetailsSEO seoData={seoData} job={state.job} />
      )}

      <div className="min-h-screen bg-background">
        {/* Main content starts directly - Navigation is handled by NavigationLayout */}
        <main className="content-container pt-4" role="main">
          <article className="container mx-auto px-4 py-8">
            
            {/* Job Header */}
            <JobDetailsHeader 
              job={state.job}
              isBookmarked={state.isBookmarked}
              hasApplied={state.hasApplied}
              applicationStatus={state.applicationStatus}
              isJobExpired={isJobExpired}
              onApply={handleApply}
              onBookmark={handleBookmark}
              onReport={handleReportJob}
              onEditJob={handleEditJob}
              formatSalary={formatSalary}
              getSalaryTooltipText={getSalaryTooltipText}
              getJobTypeLabel={getJobTypeLabel}
              getExperienceLabel={getExperienceLabel}
              userProfile={userProfile}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <main className="lg:col-span-2 space-y-8">
                {/* Job Description */}
                <section aria-labelledby="job-description">
                  <JobDescription job={state.job} />
                </section>

                {/* Requirements */}
                <section aria-labelledby="job-requirements">
                  <JobRequirements job={state.job} />
                </section>
              </main>

              {/* Sidebar */}
              <aside className="space-y-8 sticky top-20 self-start">
                {/* Company Info */}
                <section aria-labelledby="company-info">
                  <CompanyInfo employer={state.job?.employer} />
                </section>

                {/* Similar Jobs */}
                {state.similarJobs.length > 0 && (
                  <section aria-labelledby="similar-jobs">
                    <SimilarJobs jobs={state.similarJobs} />
                  </section>
                )}
              </aside>
            </div>
          </article>
        </main>

      {/* Application Modal */}
      {state.showApplicationModal && (
        <ApplicationModal
          job={state.job}
          userProfile={userProfile}
          onClose={() => dispatch({ type: 'TOGGLE_APPLICATION_MODAL', payload: false })}
          onSubmit={handleApplicationSubmit}
        />
      )}

      {/* Report Job Modal */}
      <ReportModal 
        show={state.showReportModal && !state.reportSuccess}
        reportError={state.reportError}
        hasReportedJob={state.hasReportedJob}
        selectedReason={state.selectedReason}
        reportSubmitting={state.reportSubmitting}
        checkingReport={state.checkingReport}
        onClose={resetReportModal}
        onSubmit={handleSubmitReport}
        onReasonChange={(reason) => dispatch({ type: 'SET_REPORT_STATE', payload: { selectedReason: reason } })}
      />

      {/* Report Success Modal */}
      <ReportSuccessModal 
        show={state.reportSuccess}
        onClose={resetReportModal}
      />

      {/* Preview Mode Banner for Unauthenticated Users */}
      {!user && (
        <div className="fixed bottom-4 left-4 right-4 z-50">
          <div className="bg-primary text-white p-4 rounded-lg shadow-lg max-w-md mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Create an Account</p>
                <p className="text-sm opacity-90">Sign in today to apply to this job</p>
              </div>
              <Button
                variant="white"
                size="sm"
                onClick={() => navigate('/login-register?redirect=' + encodeURIComponent(window.location.pathname))}
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
});

export default JobDetails;

